import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Clock } from "lucide-react";
import Seo from "../components/site/Seo";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import { SectionLabel, PrimaryButton, ArrowLink } from "../components/site/Bits";
import { Field, TextAreaField, SelectField, FormSuccess } from "../components/site/Fields";
import { SEO, CONTACT, COMPANY, VERTICALS } from "../lib/content";
import { IMAGES } from "../lib/images";
import { postEnquiry, extractError } from "../lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/;

const emptyForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: "",
  requirement: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Please enter your name.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Please enter a valid email address.";
    if (form.phone.trim().length < 6) next.phone = "Please enter a contact number.";
    if (!form.service) next.service = "Please choose a service.";
    if (form.requirement.trim().length < 3) next.requirement = "Please tell us about your project or requirement.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await postEnquiry(form);
      setSent(true);
      setForm(emptyForm);
      toast.success("Your enquiry has reached the LYKIT team.");
    } catch (error) {
      toast.error(extractError(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo title={SEO.contact.title} description={SEO.contact.description} path="/contact" />

      <PageHero
        eyebrow="Contact"
        titleLines={[CONTACT.heading]}
        intro={CONTACT.text.join(" ")}
        image={IMAGES.contact.hero}
        compact
      />

      <section className="lk-section" data-testid="contact-section">
        <div className="lk-container-wide">
          <div className="grid gap-y-10 lg:grid-cols-12 lg:gap-x-16">
            {/* details */}
            <div className="lg:col-span-4">
              <Reveal>
                <SectionLabel>Studio</SectionLabel>
              </Reveal>
              <Reveal delay={0.06}>
                <h2 className="display-sm mt-7" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {COMPANY.legalName}
                </h2>
              </Reveal>

              <Reveal delay={0.1}>
                <div className="mt-9 space-y-8">
                  <div className="flex gap-4">
                    <MapPin size={17} strokeWidth={1.6} style={{ color: "#B08A4B", marginTop: 3 }} aria-hidden="true" />
                    <div>
                      <span className="eyebrow block">Address</span>
                      <a
                        href={COMPANY.mapsDirections}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-address-link"
                        className="mt-3 block text-[15px] leading-[1.62] transition-colors duration-200 hover:text-violet"
                      >
                        {COMPANY.addressLines.map((line) => (
                          <span key={line} className="block">
                            {line}
                          </span>
                        ))}
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Mail size={17} strokeWidth={1.6} style={{ color: "#B08A4B", marginTop: 3 }} aria-hidden="true" />
                    <div>
                      <span className="eyebrow block">Email</span>
                      <a
                        href={`mailto:${COMPANY.email}`}
                        data-testid="contact-email-link"
                        className="mt-3 block text-[15px] transition-colors duration-200 hover:text-violet"
                      >
                        {COMPANY.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Clock size={17} strokeWidth={1.6} style={{ color: "#B08A4B", marginTop: 3 }} aria-hidden="true" />
                    <div>
                      <span className="eyebrow block">Working Hours</span>
                      <p className="mt-3 text-[15px] leading-[1.62] body-muted">
                        Monday &ndash; Saturday
                        <br />
                        10:00 &ndash; 19:00 IST
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.16}>
                <div className="mt-10 pt-8" style={{ borderTop: "1px solid rgba(23,21,34,0.10)" }}>
                  <span className="eyebrow block">Follow</span>
                  <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {COMPANY.social.map((social) => (
                      <li key={social.label}>
                        <a
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`contact-social-${social.label.toLowerCase()}`}
                          className="text-[14px] transition-colors duration-200 hover:text-violet"
                          style={{ color: "rgba(23,21,34,0.68)" }}
                        >
                          {social.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>

            {/* form */}
            <div className="lg:col-span-7 lg:col-start-6">
              {sent ? (
                <FormSuccess
                  testId="contact-form-success"
                  title="ENQUIRY RECEIVED."
                  message="Thank you. Your enquiry has reached the LYKIT team and we will get in touch with you shortly."
                >
                  <div className="flex flex-wrap items-center gap-6">
                    <PrimaryButton variant="outline" onClick={() => setSent(false)} data-testid="contact-form-reset">
                      Send Another Enquiry
                    </PrimaryButton>
                    <ArrowLink href={`mailto:${COMPANY.email}`}>{COMPANY.email}</ArrowLink>
                  </div>
                </FormSuccess>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-enquiry-form" noValidate>
                  <Reveal>
                    <SectionLabel>Enquiry</SectionLabel>
                  </Reveal>
                  <div className="mt-8 grid gap-6 sm:grid-cols-2">
                    <Field
                      label="Name"
                      name="name"
                      required
                      autoComplete="name"
                      value={form.name}
                      onChange={onChange}
                      error={errors.name}
                      placeholder="Your full name"
                    />
                    <Field
                      label="Company / Organisation"
                      name="company"
                      autoComplete="organization"
                      value={form.company}
                      onChange={onChange}
                      placeholder="Optional"
                    />
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      value={form.email}
                      onChange={onChange}
                      error={errors.email}
                      placeholder="you@company.com"
                    />
                    <Field
                      label="Phone"
                      name="phone"
                      required
                      autoComplete="tel"
                      value={form.phone}
                      onChange={onChange}
                      error={errors.phone}
                      placeholder="+91"
                    />
                    <div className="sm:col-span-2">
                      <SelectField
                        label="Service Interested In"
                        name="service"
                        required
                        value={form.service}
                        onChange={onChange}
                        options={CONTACT.serviceOptions}
                        error={errors.service}
                        placeholder="Select a service"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <TextAreaField
                        label="Project / Requirement"
                        name="requirement"
                        required
                        rows={4}
                        value={form.requirement}
                        onChange={onChange}
                        error={errors.requirement}
                        placeholder="Tell us what you're building"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <TextAreaField
                        label="Message"
                        name="message"
                        rows={3}
                        value={form.message}
                        onChange={onChange}
                        placeholder="Anything else we should know?"
                      />
                    </div>
                  </div>

                  <div className="mt-9 flex flex-wrap items-center gap-8">
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-send-enquiry-button"
                      className="btn btn-primary"
                      style={sending ? { opacity: 0.7, cursor: "wait" } : undefined}
                    >
                      {sending ? "Sending…" : CONTACT.submitLabel}
                    </button>
                    <p className="text-[12.5px]" style={{ color: "rgba(23,21,34,0.5)" }}>
                      Prefer email? Write to{" "}
                      <a
                        href={`mailto:${COMPANY.email}`}
                        data-testid="contact-form-email-link"
                        style={{ color: "#352A59", textDecoration: "underline" }}
                      >
                        {COMPANY.email}
                      </a>
                    </p>
                  </div>
                </form>
              )}

              <div className="mt-10 grid sm:grid-cols-2">
                {VERTICALS.map((vertical) => (
                  <a
                    key={vertical.id}
                    href={vertical.to}
                    className="px-1 py-5 transition-colors duration-300 sm:px-5 sm:first:pl-1"
                    style={{ borderTop: "1px solid rgba(23,21,34,0.12)" }}
                  >
                    <span
                      className="font-display text-[11px] font-bold"
                      style={{ letterSpacing: "0.16em", color: "rgba(23,21,34,0.36)" }}
                    >
                      {vertical.num}
                    </span>
                    <span className="mt-1 block font-display text-[15px] font-extrabold tracking-[-0.01em]">
                      {vertical.menuTitle}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* map */}
      <section style={{ background: "var(--lykit-lavender)" }} data-testid="contact-map-section">
        <div className="lk-container-wide py-[clamp(36px,3.8vw,62px)]">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <SectionLabel>{CONTACT.mapHeading}</SectionLabel>
              <h2 className="display-md mt-6 max-w-[24ch]">R.T. NAGAR, BENGALURU.</h2>
            </div>
            <a
              href={COMPANY.mapsDirections}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-get-directions-link"
              className="btn btn-outline"
            >
              {CONTACT.directionsLabel}
            </a>
          </div>

          <div
            className="mt-10 overflow-hidden rounded-[20px]"
            style={{ border: "1px solid rgba(23,21,34,0.12)", background: "#fff" }}
          >
            <iframe
              title="LYKIT Entertainment office location on Google Maps"
              src={COMPANY.mapsEmbed}
              data-testid="contact-map-embed"
              width="100%"
              height="460"
              style={{ border: 0, display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>

          <p className="mt-6 text-[13.5px] body-muted">{COMPANY.addressFull}</p>
        </div>
      </section>
    </>
  );
}
